package com.wintermar.inspection.feature.data.repository

import com.apollographql.apollo.ApolloClient
import com.wintermar.inspection.AppDatabase
import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.core.domain.Result
import com.wintermar.inspection.core.domain.handleAsFlow
import com.wintermar.inspection.feature.data.local.db.SqliteCompat
import com.wintermar.inspection.feature.data.remote.mapper.template.toListTemplateOptionModel
import com.wintermar.inspection.feature.data.remote.mapper.template.toTemplateOptionModel
import com.wintermar.inspection.feature.data.remote.request.HeaderRequest
import com.wintermar.inspection.feature.domain.model.VesselModel
import com.wintermar.inspection.feature.domain.model.auth.UserModel
import com.wintermar.inspection.feature.domain.model.template.TemplateOptionModel
import com.wintermar.inspection.feature.domain.repository.ReferenceRepository
import com.wintermar.inspection.graphql.AllVesselNameQuery
import com.wintermar.inspection.graphql.FileUrlQuery
import com.wintermar.inspection.graphql.InspectionTemplateOptionsQuery
import com.wintermar.inspection.graphql.ProfileQuery
import com.wintermar.inspection.graphql.type.FileCategory
import com.wintermar.inspection.graphql.type.GetRemoteFileQueryInput
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOn
import kotlinx.coroutines.withContext

class ReferenceRepositoryImpl(
    private val apolloClient: ApolloClient,
    private val appDatabase: AppDatabase
) : ReferenceRepository {
    override fun getVesselList(): Flow<FlowResult<List<VesselModel>>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .query(
                        AllVesselNameQuery()
                    ).addHttpHeader(
                        name = HeaderRequest.OPERATION,
                        value = AllVesselNameQuery.OPERATION_NAME
                    )
                    .execute()
            },
            response = { response ->
                response.data?.allVesselName?.map { vessel ->
                    VesselModel(
                        id = vessel.id.toString(),
                        name = vessel.name
                    )
                } ?: emptyList()
            }
        )
    }

    override fun getVesselsFromLocalDb(): Flow<FlowResult<List<VesselModel>>> {
        return flow {
            emit(FlowResult.Loading(showLoading = true))
            try {
                val result = appDatabase.transactionWithResult {
                    val queries = appDatabase.vesselTableQueries
                    queries.getAllVessel().executeAsList().map { vessel ->
                        VesselModel(
                            id = vessel.id,
                            name = vessel.name
                        )
                    }
                }
                emit(FlowResult.Success(result))
            } catch (e: Exception) {
                emit(FlowResult.Error(e))
            }
            emit(FlowResult.Loading(showLoading = false))
        }.flowOn(Dispatchers.Default)
    }

    override suspend fun insertVesselList(vessels: List<VesselModel>): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    val executableQuery = SqliteCompat.selectInsertImplementation(
                        appDatabase = appDatabase,
                        upsert = appDatabase.vesselTableQueries::insertVessel,
                        replace = appDatabase.vesselTableQueries::insertReplaceVessel
                    )

                    vessels.forEach { vessel ->
                        executableQuery(
                            vessel.id,
                            vessel.name,
                        )
                    }
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override fun getTemplateOptions(): Flow<FlowResult<List<TemplateOptionModel>>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .query(
                        InspectionTemplateOptionsQuery()
                    ).addHttpHeader(
                        name = HeaderRequest.OPERATION,
                        value = InspectionTemplateOptionsQuery.OPERATION_NAME
                    )
                    .execute()
            },
            response = { response ->
                response.data?.toTemplateOptionModel() ?: emptyList()
            }
        )
    }

    override fun getTemplateOptionsFromLocalDb(): Flow<FlowResult<List<TemplateOptionModel>>> {
        return flow {
            emit(FlowResult.Loading(showLoading = true))
            try {
                val result = appDatabase.transactionWithResult {
                    val queries = appDatabase.templateOptionTableQueries
                    queries.getAnswerOptions().executeAsList().toListTemplateOptionModel()
                }
                emit(FlowResult.Success(result))
            } catch (e: Exception) {
                emit(FlowResult.Error(e))
            }
            emit(FlowResult.Loading(showLoading = false))
        }.flowOn(Dispatchers.Default)
    }

    override suspend fun insertTemplateOptions(options: List<TemplateOptionModel>): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    val executableQuery = SqliteCompat.selectInsertImplementation(
                        appDatabase = appDatabase,
                        upsert = appDatabase.templateOptionTableQueries::insertAnswerOption,
                        replace = appDatabase.templateOptionTableQueries::insertReplaceAnswerOption
                    )

                    options.forEach { option ->
                        executableQuery(
                            option.id,
                            option.order.toLong(),
                            option.label,
                            option.answerType.name,
                        )
                    }
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override fun getFileUrl(fileName: String): Flow<FlowResult<String>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .query(
                        FileUrlQuery(
                            GetRemoteFileQueryInput(
                                fileName = fileName,
                                category = FileCategory.INSPECTIONS
                            )
                        )
                    ).addHttpHeader(
                        name = HeaderRequest.OPERATION,
                        value = FileUrlQuery.OPERATION_NAME
                    )
                    .execute()
            },
            response = { response ->
                response.data?.fileUrl?.fileUrl ?: ""
            }
        )
    }

    override fun getProfile(): Flow<FlowResult<UserModel>> {
        return handleAsFlow(
            request = {
                apolloClient
                    .query(
                        ProfileQuery()
                    ).addHttpHeader(
                        name = HeaderRequest.OPERATION,
                        value = ProfileQuery.OPERATION_NAME
                    )
                    .execute()
            },
            response = { response ->
                val data = response.data?.profile
                UserModel(
                    userId = data?.id.toString(),
                    fullName = data?.fullName.toString(),
                    listAccess = data?.accesses?.readWrite ?: listOf()
                )
            }
        )
    }
}
